import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {BLOCK_EDITOR_TOP_ROW_HEIGHT, PANE_MARGIN} from 'insights/InsightsConstants';
import {USER_DEFINED_VIEW_SCHEMA} from 'insights/warehouse/AnalyticsWarehouseConstants';
import {WarehouseUserDefinedViewMetadataFull} from 'insights/warehouse/warehouseModelsDataLayout';
import {schemaQualifiedTableNameToSchemaAndCompleteTableNames} from 'insights/warehouse/WarehouseUtils';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import HoverIcon from 'nav/expanded/HoverIcon';
import notifications from 'notifications';
import {ellipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipPlacement} from 'ui-library/bs/Tooltip';
import ClipboardText from 'ui-library/ClipboardText';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER, RED_LIGHTER, RED_LIGHTEST} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import LoadingPaneContents from 'ui-library/pane/LoadingPaneContents';
import Pane from 'ui-library/pane/Pane';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';
import {deleteUserDefinedView, refreshUserDefinedView} from 'warehouse/WarehouseDAO';

import OpenEntityDiagramButton from './OpenEntityDiagramButton';
import trackOnCopy from './trackOnCopy';

const PANE_TITLE = 'Schema browser';
export const MISSING_VIEW_TOOLTIP_TEXT =
  'This custom view does not exist in the warehouse. If you have just created it, it may take a few minutes to sync. Otherwise, the view has been dropped, and you may delete it and recreate one with an updated query.';

interface Actions {
  openTable: (fullTableName: string) => void;
  setQuery: (query: string) => void;
  onDelete?: (viewId: string) => Promise<void>;
  onRefresh?: (existsInWarehouse: boolean, viewId: string) => Promise<void>;
}

function useTrackOnSearch(query: string, numResults: number): void {
  const debouncedValue = useMemo(() => ({query, numResults}), [query, numResults]);
  const {query: debouncedQuery, numResults: debouncedNumResults} = useDebounce(debouncedValue, 1000);
  useEffect(() => {
    if (debouncedQuery.length) {
      deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.SEARCHED_IN_SCHEMA_BROWSER, {
        query_length: debouncedQuery.length,
        num_results: debouncedNumResults,
      });
    }
  }, [debouncedQuery, debouncedNumResults]);
}

export function WarehouseTableRow({
  actions,
  fullTableName,
  userDefinedViewData,
}: {
  actions: Pick<Actions, 'openTable' | 'onDelete' | 'onRefresh'>;
  fullTableName: string;
  userDefinedViewData?: WarehouseUserDefinedViewMetadataFull;
}): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const [schemaName, completeTableName] =
    schemaQualifiedTableNameToSchemaAndCompleteTableNames(fullTableName);

  const isUserDefinedView = userDefinedViewData?.id != null;
  const viewDoesNotExist = isUserDefinedView && userDefinedViewData?.existsInWarehouse === false;

  const handleDeleteClick = (): void => {
    if (actions.onDelete && userDefinedViewData?.id) {
      actions.onDelete(userDefinedViewData.id);
    }
  };

  const handleRefreshClick = (): void => {
    if (actions.onRefresh && userDefinedViewData?.id) {
      actions.onRefresh(userDefinedViewData.existsInWarehouse, userDefinedViewData.id);
    }
  };

  const icon = isUserDefinedView ? 'head-side-gear' : 'warehouse-table';

  return (
    <div
      aria-label={fullTableName}
      className={classNames(
        css(styles.warehouseTableRow, viewDoesNotExist && styles.warehouseTableRowMissing),
        'u-vertical-center-items'
      )}
      {...getActionButtonRoleProps({
        onPress: () => {
          actions.openTable(fullTableName);
        },
      })}
      {...hoverProps}
      {...getTestProps({component: 'WarehouseTableRow', element: 'root', key: fullTableName})}
    >
      <ClipboardText
        clipboardData={fullTableName}
        onClick={trackOnCopy}
        copyIconAlwaysVisible={isHovered}
        disableTooltip={!isHovered}
        tooltipPlacement={FloatingPlacement.TOP}
        className={classNames('u-vertical-center-items', css(styles.clipboardTextWrapper))}
      >
        <Tooltip
          text={MISSING_VIEW_TOOLTIP_TEXT}
          placement={FloatingPlacement.TOP}
          isDisabled={!viewDoesNotExist}
        >
          <span className="u-vertical-center-items u-display-inline-flex">
            <>
              <BenchlingIcon
                className={classNames(css(styles.warehouseTableIcon), 'u-mini-spacing-right')}
                icon={icon}
              />
              <div
                className={css(styles.ellipsisText)}
                {...getTestProps({
                  component: 'WarehouseTableRow',
                  element: 'tableName',
                  key: fullTableName,
                })}
              >
                {fullTableName}
              </div>
            </>
          </span>
        </Tooltip>
        {isHovered && (
          <OpenEntityDiagramButton
            schemaName={schemaName}
            tableName={completeTableName}
            useHoverIcon={true}
            tooltipPlacement={TooltipPlacement.TOP}
          />
        )}
      </ClipboardText>
      {isHovered && isUserDefinedView && actions.onRefresh && (
        <Tooltip text="Refresh custom view status" placement={FloatingPlacement.TOP}>
          <HoverIcon
            aria-label="Refresh custom view status"
            icon="refresh"
            className={css(styles.refreshButton)}
            dataTestAttrs={{
              component: 'WarehouseTableRow',
              element: 'refreshButton',
              key: fullTableName,
            }}
            onClick={handleRefreshClick}
          />
        </Tooltip>
      )}
      {isHovered && isUserDefinedView && userDefinedViewData?.permissions.deletable && actions.onDelete && (
        <Tooltip text="Delete custom view" placement={FloatingPlacement.TOP}>
          <HoverIcon
            aria-label="Delete custom view"
            icon="trash-can"
            className={css(styles.deleteButton)}
            dataTestAttrs={{
              component: 'WarehouseTableRow',
              element: 'deleteButton',
              key: fullTableName,
            }}
            onClick={handleDeleteClick}
          />
        </Tooltip>
      )}
    </div>
  );
}

const ROW_LIMIT = 100;

export interface WarehouseTableListingProps {
  actions: Actions;
  additionalActionButtons?: JSX.Element;
  matchedSortedFullTableNames: Array<string>;
  query: string;
  userDefinedViewsData?: Array<WarehouseUserDefinedViewMetadataFull>;
}

export default function WarehouseTableListing({
  actions,
  additionalActionButtons,
  matchedSortedFullTableNames,
  query,
  userDefinedViewsData,
}: WarehouseTableListingProps): JSX.Element {
  useTrackOnSearch(query, matchedSortedFullTableNames.length);
  const [rowsOffset, setRowsOffset] = useState(0);
  const rowListWrapperRef = useRef<HTMLDivElement>(null);

  const userDefinedViewDataByFullTableName = useMemo(() => {
    const map = new Map<string, WarehouseUserDefinedViewMetadataFull>();
    if (userDefinedViewsData) {
      for (const view of userDefinedViewsData) {
        const fullTableName = `${USER_DEFINED_VIEW_SCHEMA}.${view.name}`;
        map.set(fullTableName, view);
      }
    }
    return map;
  }, [userDefinedViewsData]);

  function resetScroll(): void {
    if (rowListWrapperRef.current) {
      rowListWrapperRef.current.scrollTop = 0;
    }
  }

  function handleChangeQuery(newQuery: string): void {
    actions.setQuery(newQuery);
    // Go back to top of first page.
    setRowsOffset(0);
    resetScroll();
  }

  async function handleDeleteView(viewId: string): Promise<void> {
    const confirmed = await runConfirmDeleteModal({
      typeName: 'custom view',
      message:
        'Are you sure you want to delete this custom view? This action cannot be undone. This action only deletes the view, not the data it contains.',
    });

    if (!confirmed) {
      return;
    }

    await withLoadingScreen(deleteUserDefinedView(viewId));
    notifications.success('Custom view deleted successfully');

    await getApolloClient().refetchQueries({
      include: ['ListWarehouseModelsQuery'],
    });
    setRowsOffset(0);
    resetScroll();
  }

  async function handleRefreshView(existsInWarehouse: boolean, viewId: string): Promise<void> {
    if (existsInWarehouse) {
      await withLoadingScreen(refreshUserDefinedView(viewId));
    }

    await getApolloClient().refetchQueries({
      include: ['ListWarehouseModelsQuery'],
    });
    notifications.success('Custom view status refreshed successfully');
  }

  const tablesToShow = matchedSortedFullTableNames.slice(rowsOffset, rowsOffset + ROW_LIMIT);

  const actionsWithDeleteAndRefresh = {
    ...actions,
    onDelete: handleDeleteView,
    onRefresh: handleRefreshView,
  };

  return (
    <Pane
      dataTestAttrs={{component: 'WarehouseTableListing', element: 'root'}}
      title={PANE_TITLE}
      fitContent={true}
      toolbar={
        <div className="u-vertical-center-items">
          <PlainSearchInput
            className={css(styles.searchInput)}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              handleChangeQuery(e.target.value);
            }}
            onEscape={() => {
              handleChangeQuery('');
            }}
            placeholder="Search"
            value={query}
          />
          {additionalActionButtons && <span className="u-mini-spacing-left">{additionalActionButtons}</span>}
        </div>
      }
    >
      <div className={classNames(css(styles.warehouseTableListing))}>
        <div
          ref={rowListWrapperRef}
          className={classNames(css(styles.rowListWrapper), 'scrollbar-override')}
          {...getTestProps({component: 'WarehouseTableListing', element: 'rowList'})}
        >
          {tablesToShow.map((fullTableName) => (
            <WarehouseTableRow
              key={fullTableName}
              actions={actionsWithDeleteAndRefresh}
              fullTableName={fullTableName}
              userDefinedViewData={userDefinedViewDataByFullTableName.get(fullTableName)}
            />
          ))}
        </div>
        {matchedSortedFullTableNames.length > ROW_LIMIT && (
          <div
            className={css(styles.pagerWrapper)}
            {...getTestProps({component: 'WarehouseTableListing', element: 'resultsPager'})}
          >
            <ResultsPager
              setOffset={(newRowsOffset) => {
                setRowsOffset(newRowsOffset);
                resetScroll();
              }}
              resultDescription={null}
              isLoading={false}
              pagination={{limit: ROW_LIMIT, offset: rowsOffset, total: matchedSortedFullTableNames.length}}
            />
          </div>
        )}
      </div>
    </Pane>
  );
}

export function LoadingWarehouseTableListing({
  additionalActionButtons,
}: Pick<WarehouseTableListingProps, 'additionalActionButtons'>): JSX.Element {
  return (
    <Pane title={PANE_TITLE} toolbar={additionalActionButtons} fitContent={true}>
      <LoadingPaneContents />
    </Pane>
  );
}

const styles = StyleSheet.create({
  clipboardTextWrapper: {
    display: 'flex',
  },
  searchInput: {
    width: 241,
  },
  warehouseTableIcon: {
    color: GRAY,
    flexShrink: 0,
  },
  refreshButton: {
    marginLeft: 4,
  },
  deleteButton: {
    marginLeft: 4,
  },
  warehouseTableListing: {
    maxHeight: BLOCK_EDITOR_TOP_ROW_HEIGHT,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  pagerWrapper: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingLeft: PANE_MARGIN,
    paddingRight: 3,
  },
  rowListWrapper: {
    overflowY: 'auto',
  },
  warehouseTableRow: {
    cursor: 'pointer',
    fontSize: 13,
    paddingTop: 3,
    paddingRight: 3,
    paddingBottom: 3,
    paddingLeft: PANE_MARGIN,
    width: '100%',
    height: 26,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  warehouseTableRowMissing: {
    backgroundColor: RED_LIGHTEST,
    ':hover': {
      backgroundColor: RED_LIGHTER,
    },
  },
  ellipsisText,
});
